import { useReducer } from 'react';
import { ParseResult, parse } from 'papaparse';
import {
  BdxStoreData,
  DeclarationMonthYear,
  ExistingMapData,
  ExistingMapOptions,
  FieldData
} from '@interfaces/BdxImporter.interface';

export interface BdxStoreType extends BdxStoreData {
  setSelectedMap: (selectedMapId: string) => void;
  setDeclarationMonthYear: (declarationMonthYear: DeclarationMonthYear) => void;
  setExistingMapOptions: (existingMapOptions: ExistingMapOptions[]) => void;
  setExistingMapData: (existingMapData: ExistingMapData) => void;
  setBdxImportedData: (bdxStoreData: File | null) => Promise<string[][] | null>;
  setFieldData: (fieldData: FieldData) => void;
  setFilePath: (filePath: string) => void;
}

export default function useBdxStore(): BdxStoreType {
  const [store, setStore] = useReducer(
    (prev: BdxStoreData, newState: Partial<BdxStoreData>) => ({
      ...prev,
      ...newState
    }),
    {
      bdxImportedData: null,
      bdxJsonData: null,
      errorMessage: '',
      filePath: '',
      fieldData: {
        fields: [],
        productTypes: [],
        categories: [],
        categoriesWithFields: [],
        importFields: [],
        importFieldExamples: {},
        autoMatchedFields: {}
      },
      existingMapOptions: [{ name: '', mapId: '' }],
      existingMapData: {},
      declarationMonthYear: { year: '', month: '' },
      selectedMapData: { id: '', label: 'Create new map' }
    }
  );

  const setBdxImportedData = async (
    bdxImportedData: File | null
  ): Promise<string[][] | null> => {
    setStore({ bdxImportedData: bdxImportedData });

    if (!bdxImportedData) {
      setStore({ bdxJsonData: null });
      return null;
    }

    return new Promise((resolve, reject) => {
      parse(bdxImportedData, {
        skipEmptyLines: true,
        complete: (results: ParseResult<string[]>) => {
          if (results.data.length < 2) {
            reject({
              message: 'You have attempted to upload an invalid/empty file.'
            });
          }
          setStore({ bdxJsonData: results.data });
          resolve(results.data);
        },
        error(error, file) {
          setStore({ bdxJsonData: null });
          reject({
            message: error.message,
            data: null
          });
        }
      });
    });
  };

  const setFieldData = (fieldData: FieldData) => {
    setStore({ fieldData: fieldData });
  };

  const setFilePath = (filePath: string) => {
    setStore({ filePath: filePath });
  };

  const setExistingMapOptions = (existingMapOptions: ExistingMapOptions[]) => {
    setStore({ existingMapOptions: existingMapOptions });
  };

  const setExistingMapData = (existingMapData: ExistingMapData) => {
    setStore({ existingMapData: existingMapData });
  };

  const setDeclarationMonthYear = (
    declarationMonthYear: DeclarationMonthYear
  ) => {
    setStore({ declarationMonthYear: declarationMonthYear });
  };

  const setSelectedMap = (selectedMap: string) => {
    const existingMap = store.existingMapOptions.find(
      (item) => item.mapId === selectedMap
    );

    setStore({
      selectedMapData: {
        id: existingMap ? existingMap.mapId : '',
        label: existingMap ? existingMap.name : 'Create new map'
      }
    });
  };

  return {
    bdxImportedData: store.bdxImportedData,
    bdxJsonData: store.bdxJsonData,
    errorMessage: store.errorMessage,
    filePath: store.filePath,
    fieldData: store.fieldData,
    existingMapOptions: store.existingMapOptions,
    existingMapData: store.existingMapData,
    declarationMonthYear: store.declarationMonthYear,
    selectedMapData: store.selectedMapData,
    setFilePath,
    setBdxImportedData,
    setFieldData,
    setExistingMapData,
    setExistingMapOptions,
    setDeclarationMonthYear,
    setSelectedMap
  };
}
